// import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Server, MapPin, Package, FileUp, Settings } from 'lucide-react';
import React from 'react';
import { MenuItem } from '@/types/menu.types';
import { MenuButton } from '@/components/ui/menu-button';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import PRtrack_050505 from '@/assets/PRtrack_050505.png';
import { ArrowLeft } from 'lucide-react';
import { eventTypeService } from '@/services/api';

interface MainMenuProps {
  mode?: 'main' | 'platform';
  backPath?: string;
}

export const MainMenu = ({ mode = 'main', backPath }: MainMenuProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getVisibleMenuItems, userRole } = usePermissions();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const [activeButton, setActiveButton] = React.useState<number | null>(null);
  const [autoCreatedCount, setAutoCreatedCount] = React.useState(0);

  const visibleItems = getVisibleMenuItems();
  const isAdmin = userRole === 'Administrador';

  React.useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await eventTypeService.countAutoCreated();
        if (!cancelled && response.success && response.data) {
          setAutoCreatedCount(response.data.count);
        }
      } catch {
        if (!cancelled) setAutoCreatedCount(0);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, mode]);

  const eventTypesHighlight =
    autoCreatedCount > 0
      ? {
          highlight: true as const,
          highlightBadge: `${autoCreatedCount} por configurar`,
        }
      : {};

  const allMenuItems: MenuItem[] = [
    {
      icon: <Users className="w-10 h-10" />,
      title: t('pages.mainMenu.userManagement.title'),
      path: '/users',
      description: t('pages.mainMenu.userManagement.description'),
    },
    {
      icon: <Server className="w-10 h-10" />,
      title: t('pages.mainMenu.deviceManagement.title'),
      path: '/devices',
      description: t('pages.mainMenu.deviceManagement.description'),
    },
    {
      icon: <MapPin className="w-10 h-10" />,
      title: t('pages.mainMenu.operationalDashboard.title'),
      path: '/operations',
      description: t('pages.mainMenu.operationalDashboard.description'),
    },
    {
      icon: <Package className="w-10 h-10" />,
      title: t('pages.mainMenu.bulkLectureInsert.title', 'Insercion masiva de lecturas'),
      path: '/lectures/bulk-insert',
      description: t(
        'pages.mainMenu.bulkLectureInsert.description',
        'Insercion masiva de lecturas de dispositivos'
      ),
      adminOnly: true,
    },
    {
      icon: <FileUp className="w-10 h-10" />,
      title: t('pages.mainMenu.importLectureFile.title', 'Importar lecturas por archivo'),
      path: '/lectures/import',
      description: t(
        'pages.mainMenu.importLectureFile.description',
        'Insercion masiva a traves de archivo JSON'
      ),
      adminOnly: true,
    },
    {
      icon: <Settings className="w-10 h-10" />,
      title: t('pages.mainMenu.platformManagement.title', 'Gestion plataforma'),
      path: '/platform',
      description: t(
        'pages.mainMenu.platformManagement.description',
        'Configuracion y gestion de tipos de eventos'
      ),
      adminOnly: true,
      ...eventTypesHighlight,
    },
  ];

  const platformMenuItem: MenuItem = {
    icon: <Settings className="w-10 h-10" />,
    title: 'Gestion de tipos de eventos',
    path: '/platform/event-types',
    description: 'Alta, baja logica y edicion visual de tipos de eventos',
    adminOnly: true,
    ...eventTypesHighlight,
  };

  const filteredMenuItems = (mode === 'platform' ? [platformMenuItem] : allMenuItems).filter((item) => {
    const itemPath = item.path.replace('/', '');
    const isVisible = visibleItems.includes(itemPath);
    const itemAdminOk = item.adminOnly ? isAdmin : true;
    return (mode === 'platform' ? true : isVisible) && itemAdminOk;
  });

  const handleMouseDown = (index: number) => {
    setActiveButton(index);
  };

  const handleMouseUp = async (path: string) => {
    setActiveButton(null);

    // Usar token check para todas las rutas importantes
    await navigateWithTokenCheck(navigate, path);
  };

  const handleMouseLeave = () => {
    setActiveButton(null);
  };

  return (
    <div className="h-[calc(100vh-4rem-1.5rem)] flex flex-col justify-center">
      <div className="w-full max-w-8xl mx-auto px-4">
        <div className="flex justify-center mb-8">
          <img src={PRtrack_050505} alt="PR Track Logo" className="h-40 w-auto" />
        </div>
        {mode === 'platform' && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => navigate(backPath ?? '/menu')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-brand-orange text-brand-orange bg-white hover:bg-brand-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al menu principal
            </button>
          </div>
        )}
        <div className="flex justify-center">
          <div
            className={`flex justify-center gap-6 max-w-8xl ${
              filteredMenuItems.length === 2 ? 'flex-nowrap' : 'flex-wrap'
            }`}
          >
            {filteredMenuItems.map((item, idx) => {
              const isActive = activeButton === idx;
              return (
                <MenuButton
                  key={item.path}
                  menuItem={item}
                  isActive={isActive}
                  handleMouseDown={handleMouseDown}
                  handleMouseUp={handleMouseUp}
                  handleMouseLeave={handleMouseLeave}
                  idx={idx}
                  totalItems={filteredMenuItems.length}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
